/**
 * Display copy for the requirements page's two tabs and their sections.
 *
 * The CLASSIFICATION lives on the backend (Reeve.Core/Compliance/RequirementSections.cs), where a test
 * locks it against the catalog so a legal refresh can't quietly file a new obligation somewhere
 * arbitrary. Every checklist row arrives carrying its own `area` and `section`; this file only supplies
 * the labels, the order and the explanation of what each group is for.
 *
 * Sections are navigation. They never change what applies to a company or at what tier — the resolved
 * category still travels on every row and still drives the ordering inside a section.
 */

import type { RequirementArea, RequirementSection } from '../lib/entities/checklist';

export const AREA_ORDER: RequirementArea[] = ['employment', 'business', 'itData'];

export const AREA_META: Record<
  RequirementArea,
  { label: string; lead: string; empty: string }
> = {
  employment: {
    label: 'People',
    lead: 'How you treat the people who work for you. If something here is missing or wrong, an employee can take it to an employment tribunal, and what it costs is not capped.',
    empty: 'Nothing here needs your attention. Everything an employee could raise is in place.',
  },
  business: {
    label: 'Business & premises',
    lead: 'What a regulator can hold you to: the HSE, HMRC, the Home Office, Companies House. Nobody sues you over these, but the fines are real and they arrive without warning.',
    empty: 'Nothing here needs your attention. Everything a regulator could ask for is in place.',
  },
  // Regulator-facing like Business, but split off because it is a different workstream, and often a
  // different person: whoever runs IT, or an outside adviser. Nobody sorts out ROPA and fire doors in
  // the same sitting. Last of the three because it is the one most often owned by someone else.
  itData: {
    label: 'IT & data',
    lead: 'What you do with personal data and the systems it sits on. The ICO enforces this, and most of it is written once and then reviewed rather than lived with daily.',
    empty: 'Nothing here needs your attention. Your data protection paperwork is in place.',
  },
};

interface SectionMeta {
  area: RequirementArea;
  label: string;
  /** What a user is actually doing when they open this group. */
  blurb: string;
  order: number;
}

/**
 * Sections in the order they appear, roughly following the shape of the employment relationship
 * (hire, pay, time off, manage, leave) and then the records behind it. Business sections lead with
 * health & safety because it is the largest and the most consequential.
 */
export const SECTION_META: Record<RequirementSection, SectionMeta> = {
  /* ------------------------------------------------------------ Employment */
    conduct: {
    area: 'employment',
    label: 'Conduct & performance',
    blurb: 'Disciplinary, grievance and capability. A tribunal expects you to have and follow appropriate procedures, and going without is a significant risk.',
    order: 1.5,
  },
  recruitment: {
    area: 'employment',
    label: 'Hiring',
    blurb: 'Adverts, selection, offers and onboarding. Getting these right heads off discrimination claims before anyone is even employed.',
    order: 6.5,
  },
  contracts: {
    area: 'employment',
    label: 'Contracts',
    blurb: 'The written terms themselves, and the clauses inside them. A missing written statement is an automatic award on top of whatever else is claimed.',
    order: 1,
  },
  pay: {
    area: 'employment',
    label: 'Pay & pensions',
    blurb: 'Payslips, minimum wage, deductions and auto-enrolment. Underpayments come back as unlawful deduction claims, often years later.',
    order: 2,
  },
  timeOff: {
    area: 'employment',
    label: 'Time off & flexibility',
    blurb: 'Holiday, jury service, time off for dependants, and flexible working requests. Holiday pay is one of the most claimed things there is.',
    order: 3,
  },
  sickness: {
    area: 'employment',
    label: 'Sickness & absence',
    blurb: 'Self-certification, fit notes, sick pay and returning to work. Handling absence badly is where disability discrimination claims usually start.',
    order: 4,
  },
  familyLeave: {
    area: 'employment',
    label: 'Family leave',
    blurb: 'Maternity, paternity, adoption, shared parental and neonatal leave, and the pay that goes with them. Getting this wrong is automatically unfair.',
    order: 5,
  },
  leaving: {
    area: 'employment',
    label: 'Leaving',
    blurb: 'Dismissal, redundancy, settlements and references. Almost every tribunal claim starts on the way out.',
    order: 7,
  },
  fairness: {
    area: 'employment',
    label: 'Fairness & inclusion',
    blurb: 'Equality, harassment and reasonable adjustments. These are what an employer relies on to show it took reasonable steps.',
    order: 8,
  },
  wellbeing: {
    area: 'employment',
    label: 'Wellbeing & support',
    blurb: 'Menopause, domestic abuse, mental health and homeworking. Not all statutory, but they are what a good employer is judged against.',
    order: 9,
  },
  peopleRecords: {
    area: 'employment',
    label: 'Employee records',
    blurb: 'Personnel files, working time and training records. Nobody claims over a record, but a record is what proves you did the rest.',
    order: 10,
  },

  /* -------------------------------------------------------------- Business */
  safety: {
    area: 'business',
    label: 'Health & safety',
    blurb: 'Risk assessments, fire, first aid, accidents and equipment. The HSE can inspect without notice and prosecute.',
    order: 11,
  },
  payroll: {
    area: 'business',
    label: 'Payroll & HMRC',
    blurb: 'Registering as an employer, RTI submissions, P60s and P45s. Late or missing filings draw automatic penalties.',
    order: 12,
  },
  screening: {
    area: 'business',
    label: 'Right to work & screening',
    blurb: 'Right-to-work checks, DBS records and sponsor licence duties. A missed check is a civil penalty per worker, whether or not anyone was working illegally.',
    order: 13,
  },
  financialCrime: {
    area: 'business',
    label: 'Financial crime & ethics',
    blurb: 'Bribery, money laundering, tax evasion and modern slavery. Mostly about having the procedures before you ever need to point at them.',
    order: 14,
  },
  companyRecords: {
    area: 'business',
    label: 'Company records',
    blurb: 'Registers and agreements the company itself has to keep, under the Companies Act and collective consultation rules.',
    order: 15,
  },

  /* ------------------------------------------------------------- IT & data */
  data: {
    area: 'itData',
    label: 'Data protection',
    blurb: 'The standing framework: what you hold, why you may hold it, who is told and how long you keep it. Written once, then reviewed.',
    order: 16,
  },
  dataRequests: {
    area: 'itData',
    label: 'Requests & breaches',
    blurb: 'What you do when something happens. These run on a clock, so the time to write them is before you need them.',
    order: 17,
  },
  devices: {
    area: 'itData',
    label: 'Devices & monitoring',
    blurb: 'Company kit, personal phones used for work, and anything watching. Usually whoever runs IT rather than whoever runs HR.',
    order: 18,
  },
};

export const SECTION_ORDER: RequirementSection[] = (
  Object.keys(SECTION_META) as RequirementSection[]
).sort((a, b) => SECTION_META[a].order - SECTION_META[b].order);

/** The sections belonging to one tab, in display order. */
export function sectionsIn(area: RequirementArea): RequirementSection[] {
  return SECTION_ORDER.filter((s) => SECTION_META[s].area === area);
}
